import React from 'react';
import {
  myWalletCreditCardDeleteConfirmed,
  myWalletDeleteCcButtonClicked,
  myWalletModalStartedLoading,
} from '@wix/bi-logger-cashier-ugc/v2';
import { ViewMode } from '@wix/cashier-common/dist/src/types/ViewMode';
import { DeviceType } from '@wix/cashier-common/dist/src/enums/DeviceType';
import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import { ICard } from '../../../../types/Card';
import { InteractionName } from '../../../../types/InteractionName';
import { getBiLogger } from '../../../../utils/biLogger';
import { fedopsLogger } from '../../../../utils/fedopsLogger';
import { t } from '../../../../utils/i18n';
import { wixSdkAdapter } from '../../../../utils/wixSdkAdapter';
import { getDeleteCardConfirmationUrl } from '../../../../utils/links';
import { Experiment } from '../../../../types/Experiment';
import { captureError } from '../../../../utils/errorReporter';
import { ModalType } from '../../../../types/ModalType';
import { ActionButtons } from './view';

const SUBSCRIPTIONS_TPA_APP_ID = '2bef2abe-7abe-43da-889c-53c1500a328c';

const MODAL_SIZE = {
  [DeviceType.Desktop]: {
    width: 504,
    height: 380,
  },
  // workaround to open modal to 100%, openModal doesn't support string values
  // https://wix.slack.com/archives/CAKBA7TDH/p1604926273350300?thread_ts=1604922926.343800&cid=CAKBA7TDH
  [DeviceType.Mobile]: {
    width: 2000,
    height: 2000,
  },
};

export interface IActionButtonsControllerProps {
  showRemoveConfirmation: boolean;
  isRemovingInProcess: boolean;
  card: ICard;
  primaryCardId: string;
  deviceType: DeviceType;
  viewMode: ViewMode;
  appSessionId?: string;

  requestRemoval: () => void;
  declineRemoval: () => void;
  deleteCard: (cardId: string) => void;
  deletePrimaryCard: (cardId: string) => void;
}

export class ActionButtonsController extends React.Component<IActionButtonsControllerProps> {
  acceptButtonRef = React.createRef<any>();
  removalButtonRef = React.createRef<any>();

  private reportDeleteCard(cardId: string) {
    getBiLogger().report(
      myWalletCreditCardDeleteConfirmed({
        cardId,
      }),
    );

    fedopsLogger.interactionStarted(InteractionName.DELETE_CARD);
  }

  private deleteCard = async () => {
    const {
      viewMode,
      card: { creditCardId },
      primaryCardId,
      deviceType,
      appSessionId,

      deleteCard,
      deletePrimaryCard,
    } = this.props;

    getBiLogger().report(myWalletDeleteCcButtonClicked({ stepName: 'finish', result: 'yes' }));

    if (viewMode === 'site') {
      if (experimentsService.isEnabled(Experiment.EnableDeleteCardModal)) {
        let subscriptionsAmount = 0;
        try {
          fedopsLogger.interactionStarted(InteractionName.GET_SUBSCRIPTIONS_AMOUNT);
          subscriptionsAmount = await new Promise<number>((resolve, reject) => {
            wixSdkAdapter.Wix.SuperApps.getPublicAPI(
              { appDefinitionId: SUBSCRIPTIONS_TPA_APP_ID },
              async (api) => resolve(await api.getNumLiveSubscriptionsBySavedPaymentMethodId(creditCardId)),
              reject,
            );
          });
          fedopsLogger.interactionEnded(InteractionName.GET_SUBSCRIPTIONS_AMOUNT);
        } catch (error) {
          captureError(error, {
            messagePrefix: `Failed to get active subscriptions amount by savedMethodId`,
            fingerprint: [`get-active-subscriptions-amount-fail`],
          });
        }
        if (subscriptionsAmount > 0) {
          getBiLogger().report(myWalletModalStartedLoading({ modalType: ModalType.DeletionCC, cardId: creditCardId }));
          fedopsLogger.interactionStarted(InteractionName.LOAD_DELETE_CARD_MODAL);

          wixSdkAdapter.openModal({
            url: getDeleteCardConfirmationUrl({
              startLoadTime: Date.now(),
              savedPaymentMethodId: creditCardId,
              appSessionId,
              subscriptionsAmount,
            }),
            width: MODAL_SIZE[deviceType].width,
            height: MODAL_SIZE[deviceType].height,
            onClose: async (data) => {},
            theme: 'BARE',
          });
          return;
        }
      }

      if (creditCardId === primaryCardId) {
        deletePrimaryCard(creditCardId);
      } else {
        deleteCard(creditCardId);
      }

      this.reportDeleteCard(creditCardId);
    }
  };

  private declineRemoval = () => {
    getBiLogger().report(myWalletDeleteCcButtonClicked({ stepName: 'finish', result: 'no' }));
    this.props.declineRemoval();
  };

  private requestRemoval = () => {
    getBiLogger().report(myWalletDeleteCcButtonClicked({ stepName: 'start' }));
    this.props.requestRemoval();
  };

  render() {
    const { card, deviceType } = this.props;
    return (
      <ActionButtons
        accessibilityLabel={`${t('app.card.details.ask-removal')} ${card.network} ${card.lastFourDigits}`}
        mobile={deviceType === DeviceType.Mobile}
        isRemovingInProcess={this.props.isRemovingInProcess}
        showRemoveConfirmation={this.props.showRemoveConfirmation}
        requestRemoval={this.requestRemoval}
        declineRemoval={this.declineRemoval}
        confirmRemoval={this.deleteCard}
        acceptButtonRef={this.acceptButtonRef}
        removalButtonRef={this.removalButtonRef}
      />
    );
  }
}
